import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, CheckCircle2, XCircle, Pencil, Receipt, Send, PawPrint, KeyRound, MapPin, Phone, Check, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, StatusBadge, Loading, EmptyState } from "@/components/common";
import AppointmentFormDialog from "@/components/AppointmentFormDialog";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtTime, money, SERVICE_LABELS, STATUS_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const NOTIFY = [["confirmation", "Appointment confirmation"], ["reminder", "Appointment reminder"], ["schedule_change", "Schedule change notice"], ["feedback_request", "Feedback / review request"]];

function ClientCard({ c }) {
  if (!c) return null;
  const rows = [[MapPin, "Address", c.address], [Phone, "Phone", c.phone], [PawPrint, "Pets", c.pets], [KeyRound, "Access", c.access_instructions], [null, "Property notes", c.property_notes], [null, "Preferences", c.preferences], [null, "Special requests", c.special_requests]];
  return (
    <Panel title="Client & property" testId="job-client-card">
      <div className="space-y-3">
        {rows.filter((r) => r[2]).map(([Icon, l, v]) => (
          <div key={l} className={`rounded-lg p-3 ${["Pets", "Access"].includes(l) ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
            <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">{Icon && <Icon className="h-3.5 w-3.5" />}{l}</div>
            <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{v}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function ChecklistRunner({ appt, isManager, onChange }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const cl = appt.checklist;

  useEffect(() => {
    if (isManager && !cl) api.get("/checklist-templates", { params: { client_id: appt.client_id } }).then((r) => setTemplates(r.data));
  }, [isManager, cl, appt.client_id]);

  const apply = async () => {
    try { const { data } = await api.post(`/appointments/${appt.id}/checklist`, { template_id: tid }); onChange(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (ri, ti, done) => {
    try { const { data } = await api.patch(`/appointments/${appt.id}/checklist`, { room_index: ri, task_index: ti, done }); onChange(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!cl) {
    return (
      <Panel title="Checklist" testId="job-checklist">
        <EmptyState icon={ListChecks} title="No checklist attached" text={isManager ? "Attach a standard, deep or client-specific checklist." : "Ask your manager to attach one."} />
        {isManager && (
          <div className="flex gap-2">
            <Select value={tid} onValueChange={setTid}><SelectTrigger className="h-11" data-testid="checklist-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.client_name ? `${t.name} (${t.client_name})` : t.name}</SelectItem>)}</SelectContent></Select>
            <Button className="h-11" disabled={!tid} onClick={apply} data-testid="apply-checklist-btn">Attach</Button>
          </div>
        )}
      </Panel>
    );
  }
  return (
    <Panel title={cl.name} testId="job-checklist" action={<span className="text-sm font-semibold text-teal-700" data-testid="checklist-percent">{cl.percent}%</span>}>
      <Progress value={cl.percent} className="h-2 mb-5" />
      <div className="space-y-5">
        {cl.rooms.map((r, ri) => (
          <div key={ri}>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{r.name} · {r.tasks.filter((t) => t.done).length}/{r.tasks.length}</div>
            <div className="space-y-1">
              {r.tasks.map((t, ti) => (
                <label key={ti} className="flex items-center gap-3 min-h-[44px] px-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`task-${ri}-${ti}`}>
                  <Checkbox checked={t.done} onCheckedChange={(v) => toggle(ri, ti, !!v)} disabled={appt.status === "canceled"} className="h-5 w-5" data-testid={`task-checkbox-${ri}-${ti}`} />
                  <span className={`text-sm ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.text}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { tz, isManager } = useAuth();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [edit, setEdit] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/appointments/${id}`).then((r) => setA(r.data)).catch((e) => { toast.error(errMsg(e)); navigate("/schedule"); });
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (status, scope = "this") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${id}/status`, { status, scope });
      setA(data.appointment);
      toast.success(`Marked ${STATUS_LABELS[status].toLowerCase()}${data.updated > 1 ? ` (${data.updated} visits)` : ""}`);
      setCancelOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const notify = async (kind) => {
    try {
      const { data } = await api.post(`/appointments/${id}/notify`, { kind });
      data.results.forEach((r) => {
        const msg = `${r.channel.toUpperCase()}: ${STATUS_LABELS[r.status] || r.status}${r.error ? ` — ${r.error}` : ""}`;
        r.status === "sent" ? toast.success(msg) : toast.warning(msg);
      });
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!a) return <Loading />;
  const open = !["completed", "canceled"].includes(a.status);

  return (
    <div data-testid="job-detail-page">
      <Link to="/schedule" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-schedule"><ArrowLeft className="h-4 w-4" />Schedule</Link>
      <PageHeader
        title={a.client_name}
        subtitle={`${fmtDateTime(a.start, tz)} – ${fmtTime(a.end, tz)} · ${SERVICE_LABELS[a.service_type]}${isManager ? ` · ${money(a.price)}` : ""}`}
        actions={<StatusBadge status={a.status} testId="job-status-badge" />}
      />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="job-actions">
        {a.status === "requested" && isManager && <Button className="h-11" disabled={busy} onClick={() => setStatus("confirmed")} data-testid="confirm-job-btn"><Check className="h-4 w-4 mr-1" />Confirm</Button>}
        {["requested", "confirmed"].includes(a.status) && <Button className="h-11" disabled={busy} onClick={() => setStatus("in_progress")} data-testid="start-job-btn"><Play className="h-4 w-4 mr-1" />Start job</Button>}
        {a.status === "in_progress" && <Button className="h-11" disabled={busy} onClick={() => setStatus("completed")} data-testid="complete-job-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Mark complete</Button>}
        {isManager && open && <Button variant="outline" className="h-11" onClick={() => setEdit(true)} data-testid="edit-job-btn"><Pencil className="h-4 w-4 mr-1" />Edit / reschedule</Button>}
        {isManager && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" className="h-11" data-testid="notify-client-btn"><Send className="h-4 w-4 mr-1" />Message client</Button></DropdownMenuTrigger>
            <DropdownMenuContent>{NOTIFY.map(([k, l]) => <DropdownMenuItem key={k} onClick={() => notify(k)} data-testid={`notify-${k}`}>{l}</DropdownMenuItem>)}</DropdownMenuContent>
          </DropdownMenu>
        )}
        {isManager && (a.invoice_id
          ? <Button variant="secondary" asChild className="h-11"><Link to={`/invoices/${a.invoice_id}`} data-testid="view-job-invoice-btn"><Receipt className="h-4 w-4 mr-1" />View invoice</Link></Button>
          : <Button variant="secondary" asChild className="h-11"><Link to={`/invoices/new?client_id=${a.client_id}&appointment_id=${a.id}&service_type=${a.service_type}&price=${a.price}`} data-testid="create-job-invoice-btn"><Receipt className="h-4 w-4 mr-1" />Create invoice</Link></Button>)}
        {isManager && open && <Button variant="outline" className="h-11 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => setCancelOpen(true)} data-testid="cancel-job-btn"><XCircle className="h-4 w-4 mr-1" />Cancel</Button>}
      </div>
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 space-y-4">
          <ChecklistRunner appt={a} isManager={isManager} onChange={setA} />
          {a.notes && <Panel title="Job notes"><p className="text-sm whitespace-pre-wrap">{a.notes}</p></Panel>}
        </div>
        <div className="space-y-4">
          <Panel title="Job details" testId="job-meta">
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-slate-500">Assigned</dt><dd>{a.assigned_name || "Unassigned"}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Duration</dt><dd>{a.duration_minutes} min</dd></div>
              {a.series_id && <div className="flex justify-between"><dt className="text-slate-500">Recurring</dt><dd className="capitalize">{a.recurrence?.frequency}</dd></div>}
              {a.started_at && <div className="flex justify-between"><dt className="text-slate-500">Started</dt><dd>{fmtTime(a.started_at, tz)}</dd></div>}
              {a.completed_at && <div className="flex justify-between"><dt className="text-slate-500">Completed</dt><dd>{fmtTime(a.completed_at, tz)}</dd></div>}
              {a.completion_minutes !== undefined && <div className="flex justify-between"><dt className="text-slate-500">Time on job</dt><dd data-testid="job-completion-time">{a.completion_minutes} min</dd></div>}
            </dl>
          </Panel>
          <ClientCard c={a.client} />
        </div>
      </div>
      {isManager && <AppointmentFormDialog open={edit} onOpenChange={setEdit} appointment={a} onSaved={load} />}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-job-dialog">
          <DialogHeader><DialogTitle className="font-display">Cancel this job?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">The client will not be notified automatically. Use “Message client” to send a schedule change notice.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="cancel-dialog-close">Keep job</Button>
            {a.series_id && <Button variant="outline" disabled={busy} onClick={() => setStatus("canceled", "future")} data-testid="cancel-future-btn">Cancel this & future</Button>}
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={() => setStatus("canceled")} data-testid="cancel-this-btn">Cancel this visit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
